import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  DialogTitle,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DialogAnimate } from "src/components/Animate";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { markNotificationAsRead } from "src/redux/slices/notifications";

// 2026-09-27 · Watcher de notificaciones del flujo QR. Antes vivía
// dentro de un `TopBar` visual (AppBar + Logo + Languages + Notifications)
// que el user reportó como "anticuado" y ocupaba 64/92px del viewport.
// Ahora este componente NO pinta topbar — solo escucha
// `notificationsReady` y abre un modal cuando hay pedidos listos,
// además de mostrar un snackbar sutil para que el usuario sepa que
// tiene algo pendiente sin necesidad del centro de notificaciones.
export default function QrNotificationsWatcher() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { notificationsReady } = useSelector((state) => state.notification);
  const [openModal, setOpenModal] = useState(false);
  const [order, setOrder] = useState(null);
  const [lastNotifiedIds, setLastNotifiedIds] = useState(new Set());

  useEffect(() => {
    const unread = (notificationsReady || []).filter((n) => !n.readed);
    if (unread.length === 0) return;
    // Solo dispara si hay al menos un id nuevo respecto a lo que ya
    // enseñamos — evita re-abrir el modal al volver a montar el
    // componente (por navegación) si el user ya lo ha cerrado sin
    // marcarlas leídas.
    const newIds = unread.filter((n) => !lastNotifiedIds.has(n.id));
    if (newIds.length === 0) return;
    setOpenModal(true);
    setLastNotifiedIds((prev) => {
      const next = new Set(prev);
      newIds.forEach((n) => next.add(n.id));
      return next;
    });
    // Snackbar de fondo — refuerzo del modal para que si el user lo
    // cierra pueda seguir viendo la señal.
    enqueueSnackbar(
      `Tienes ${unread.length} pedido${unread.length === 1 ? "" : "s"} listo${
        unread.length === 1 ? "" : "s"
      }`,
      { variant: "success", autoHideDuration: 3500 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsReady]);

  const displayProductosOrderReady = () => (
    <TableContainer>
      <Table size="dense">
        <TableHead>
          <TableRow>
            <TableCell align="center">Id</TableCell>
            <TableCell align="center">Unidades</TableCell>
            <TableCell align="center">Producto</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(notificationsReady || [])
            .filter((n) => (order ? n?.index === order : !n.readed))
            .map((n, i) =>
              (n.productos || []).map((p, index) => (
                <TableRow key={`${i}-${index}`}>
                  <TableCell align="center">
                    <Typography variant="body2">{n.id}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{p.unidades}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{p.nombre}</Typography>
                  </TableCell>
                </TableRow>
              )),
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <DialogAnimate
      open={openModal}
      onClose={() => {
        setOpenModal(false);
        setOrder(null);
      }}
    >
      <DialogTitle>No esperes más por tus pedidos</DialogTitle>
      <Stack spacing={3} sx={{ p: 3 }}>
        <Typography variant="body2">
          Los siguientes pedidos ya están listos. ¡Que no se enfríen!
        </Typography>
      </Stack>
      <DialogContent>{displayProductosOrderReady()}</DialogContent>
      <Stack spacing={3} sx={{ p: 3 }}>
        <Typography variant="caption">
          Puedes consultar el historial completo desde el centro de
          notificaciones cuando quieras.
        </Typography>
      </Stack>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={() => {
            setOpenModal(false);
            setOrder(null);
            dispatch(markNotificationAsRead());
          }}
        >
          Entendido
        </Button>
      </DialogActions>
    </DialogAnimate>
  );
}
